import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MSym } from '@storeo/ui';
import { api } from '../../lib/api';

const FORM = 'flex flex-col gap-space-md';
const FIELD =
  'w-full px-space-md py-space-sm rounded-lg bg-surface-container-lowest text-on-surface font-body-md text-body-md placeholder:text-outline focus:outline-none focus:bg-surface-container transition-all shadow-inner';
const LABEL = 'flex flex-col gap-space-2xs font-label-sm text-label-sm text-on-surface-variant font-medium';
const BTN = 'self-start px-space-lg py-space-xs rounded-xl bg-primary text-on-primary font-label-lg text-label-lg font-bold disabled:opacity-60';
const MUTED = 'font-body-sm text-body-sm text-on-surface-variant';
const OK = 'font-body-sm text-body-sm text-secondary';
const ERR = 'rounded-lg bg-error-container/25 text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm';

/** P8 — configurar a entrega do canal escolhido. Um painel por canal (ADR 0019 §5 L2).
 *  Reskin Lumina (Fluxo 2 Fase 1); toda a lógica funcional é a mesma. */
export function ProdutoEntrega() {
  const { id } = useParams<{ id: string }>();
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.products() });
  const product = products.data?.find((p) => p.id === id);

  return (
    <div className="lumina flex flex-col gap-space-lg text-on-surface">
      <div className="flex flex-col gap-space-3xs">
        <Link to="/produtos" className="font-label-sm text-label-sm text-outline hover:text-on-surface flex items-center gap-1 w-fit">
          <MSym name="arrow_back" className="text-[16px]" /> Produtos
        </Link>
        <h1 className="font-headline-lg text-headline-lg text-on-surface tracking-tight">{product?.name ?? 'Entrega'}</h1>
        <p className="font-body-md text-body-md text-on-surface-variant">Como o cliente recebe depois de pagar.</p>
      </div>

      {!id ? null : products.isLoading ? (
        <p className={MUTED}>Carregando…</p>
      ) : !product ? (
        <p className={MUTED}>Produto não encontrado.</p>
      ) : (
        <div className="rounded-2xl bg-surface-container-low p-space-lg shadow-md">
          {product.deliveryChannel === 'digital_file' ? <FileConfig productId={id} /> : null}
          {product.deliveryChannel === 'digital_link' ? <LinkConfig productId={id} /> : null}
          {product.deliveryChannel === 'video' ? <VideoConfig productId={id} /> : null}
          {product.deliveryChannel === 'telegram' ? <TelegramConfig productId={id} /> : null}
          {product.deliveryChannel === 'physical' ? <PhysicalConfig /> : null}
        </div>
      )}
    </div>
  );
}

function FileConfig({ productId }: { productId: string }) {
  const [sentName, setSentName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadDeliveryFile(productId, file),
    onSuccess: (_r, file) => {
      setSentName(file.name);
      setError(null);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Falha no envio'),
  });

  return (
    <div className={FORM}>
      <p className={MUTED}>O cliente baixa este arquivo por um link assinado depois de pagar.</p>
      <label className={LABEL}>
        Arquivo
        <input
          className={FIELD}
          type="file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
          }}
        />
      </label>
      {upload.isPending ? <p className={MUTED}>Enviando…</p> : null}
      {sentName ? <p className={OK}>✓ {sentName} enviado — já está valendo pros próximos pedidos.</p> : null}
      {error ? <p className={ERR}>{error}</p> : null}
    </div>
  );
}

function LinkConfig({ productId }: { productId: string }) {
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api.setDeliveryLink(productId, value),
    onSuccess: () => {
      setSaved(true);
      setError(null);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Falha ao salvar'),
  });

  return (
    <form
      className={FORM}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <label className={LABEL}>
        Link ou instrução que o cliente recebe
        <textarea className={FIELD} rows={3} required value={value} onChange={(e) => setValue(e.target.value)} />
      </label>
      {error ? <p className={ERR}>{error}</p> : null}
      {saved ? <p className={OK}>✓ Salvo — já está valendo pros próximos pedidos.</p> : null}
      <button className={BTN} type="submit" disabled={save.isPending}>
        {save.isPending ? 'Salvando…' : 'Salvar'}
      </button>
    </form>
  );
}

function VideoConfig({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const videos = useQuery({ queryKey: ['videos', productId], queryFn: () => api.videos(productId) });
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadVideo(productId, file, title || undefined),
    onSuccess: () => {
      setTitle('');
      setError(null);
      qc.invalidateQueries({ queryKey: ['videos', productId] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Falha no envio'),
  });

  return (
    <div className={FORM}>
      <p className={MUTED}>O cliente assiste na área do cliente da loja (/conta) — sem download.</p>
      <label className={LABEL}>
        Título do vídeo
        <input className={FIELD} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Aula 1 — Introdução" />
      </label>
      <label className={LABEL}>
        Arquivo de vídeo
        <input
          className={FIELD}
          type="file"
          accept="video/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
          }}
        />
      </label>
      {upload.isPending ? <p className={MUTED}>Enviando…</p> : null}
      {error ? <p className={ERR}>{error}</p> : null}

      <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold mt-space-xs">Vídeos enviados</h2>
      {!videos.data?.length ? (
        <p className="font-body-sm text-body-sm text-outline">Nenhum vídeo ainda.</p>
      ) : (
        <ol className="flex flex-col gap-space-2xs pl-space-md list-decimal">
          {videos.data.map((v) => (
            <li key={v.id} className={MUTED}>
              {v.title} <span className="text-outline">· {(v.sizeBytes / 1024 / 1024).toFixed(1)} MB</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function TelegramConfig({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const [chatId, setChatId] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const current = useQuery({ queryKey: ['telegramGroup', productId], queryFn: () => api.telegramGroup(productId) });
  const link = useMutation({
    mutationFn: () => api.linkTelegramGroup(productId, chatId, title),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['telegramGroup', productId] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Falha ao vincular'),
  });

  return (
    <div className={FORM}>
      <p className={MUTED}>
        O cliente entra no grupo automaticamente depois de pagar. Pegue o <b>chat ID</b> do grupo — encaminhe uma
        mensagem dele pro <span className="font-mono text-on-surface">@userinfobot</span> no Telegram.
      </p>

      {current.data ? (
        <div className="rounded-xl bg-surface-container p-space-md flex flex-col gap-space-2xs">
          <p className={MUTED}>
            Vinculado a <b className="text-on-surface">{current.data.title}</b>
          </p>
          <span
            className={`px-2 py-0.5 rounded-full font-label-sm text-label-sm font-bold w-fit ${
              current.data.botIsAdmin ? 'bg-secondary-container/25 text-secondary' : 'bg-[#f59e0b]/15 text-[#f59e0b]'
            }`}
          >
            {current.data.botIsAdmin ? 'Bot é admin' : 'Bot ainda não é admin do grupo'}
          </span>
        </div>
      ) : null}

      <form
        className={FORM}
        onSubmit={(e) => {
          e.preventDefault();
          link.mutate();
        }}
      >
        <label className={LABEL}>
          Chat ID do grupo
          <input className={FIELD} required value={chatId} onChange={(e) => setChatId(e.target.value)} placeholder="-1001234567890" />
        </label>
        <label className={LABEL}>
          Nome do grupo
          <input className={FIELD} required value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        {error ? <p className={ERR}>{error}</p> : null}
        <button className={BTN} type="submit" disabled={link.isPending}>
          {link.isPending ? 'Vinculando…' : 'Vincular grupo'}
        </button>
      </form>
    </div>
  );
}

function PhysicalConfig() {
  return (
    <p className={MUTED}>
      Nada a configurar aqui — você recebe o endereço do cliente no pedido e marca como enviado em{' '}
      <Link to="/vendas" className="text-primary hover:underline">
        Vendas
      </Link>
      .
    </p>
  );
}
